import React, { useEffect, useState, useCallback, useRef } from 'react'
import { useHistory } from 'react-router-dom'
import { useSnackbar } from 'react-simple-snackbar'
import { fetchCorporateDashboard, deleteCorporateCampaign } from '../../api/index'
import { useCorporateGuard } from '../../hooks/useRoleGuard'
import Spinner from '../Spinner/Spinner'
import ConfirmModal from '../ConfirmModal/ConfirmModal'
import styles from '../Dashboard/Dashboard.module.css'

const MONTHS = [
  '', 'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
]

const PAGE_SIZE_OPTIONS = [5, 10, 20, 50]

const now = new Date()
const CURRENT_MONTH = now.getMonth() + 1
const CURRENT_YEAR = now.getFullYear()

const toMonthInput = (year, month) =>
  `${year}-${String(month).padStart(2, '0')}`

const formatPeriod = (period) => {
  if (!period) return '—'
  const [year, month] = period.split('-')
  return `${MONTHS[Number(month)]} ${year}`
}

const CorporateDashboard = () => {
  const history = useHistory()
  const [openSnackbar] = useSnackbar()
  const openSnackbarRef = useRef(openSnackbar)
  openSnackbarRef.current = openSnackbar
  useCorporateGuard()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [pageLoading, setPageLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [selectedMonth, setSelectedMonth] = useState(CURRENT_MONTH)
  const [selectedYear, setSelectedYear] = useState(CURRENT_YEAR)
  const [useCustomRange, setUseCustomRange] = useState(false)
  const [rangeStart, setRangeStart] = useState('')
  const [rangeEnd, setRangeEnd] = useState('')
  const [errorsOnly, setErrorsOnly] = useState(false)
  const [selectedIds, setSelectedIds] = useState([])
  const [deleting, setDeleting] = useState(false)
  const [confirmModal, setConfirmModal] = useState(null)
  const hasLoadedOnce = useRef(false)

  const load = useCallback(async () => {
    if (useCustomRange && rangeStart && rangeEnd && rangeStart > rangeEnd) {
      return
    }

    if (hasLoadedOnce.current) setPageLoading(true)
    else setLoading(true)
    try {
      const params = {
        page,
        limit: pageSize,
        errorsOnly,
      }
      if (useCustomRange && rangeStart && rangeEnd) {
        params.periodStart = rangeStart
        params.periodEnd = rangeEnd
      } else {
        params.month = selectedMonth
        params.year = selectedYear
      }
      const { data: result } = await fetchCorporateDashboard(params)
      setData(result)
      hasLoadedOnce.current = true
    } catch (error) {
      openSnackbarRef.current(error?.response?.data?.message || 'Erreur chargement')
    } finally {
      setLoading(false)
      setPageLoading(false)
    }
  }, [page, pageSize, selectedMonth, selectedYear, useCustomRange, rangeStart, rangeEnd, errorsOnly])

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, selectedMonth, selectedYear, useCustomRange, rangeStart, rangeEnd, errorsOnly])

  useEffect(() => {
    setSelectedIds([])
  }, [page, errorsOnly, pageSize, selectedMonth, selectedYear, useCustomRange, rangeStart, rangeEnd])

  useEffect(() => {
    setPage(1)
  }, [errorsOnly, pageSize, selectedMonth, selectedYear, useCustomRange, rangeStart, rangeEnd])

  const goToCurrentMonth = () => {
    setUseCustomRange(false)
    setRangeStart('')
    setRangeEnd('')
    setSelectedMonth(CURRENT_MONTH)
    setSelectedYear(CURRENT_YEAR)
  }

  const enableCustomRange = () => {
    setUseCustomRange(true)
    setRangeStart(toMonthInput(selectedYear, selectedMonth))
    setRangeEnd(toMonthInput(selectedYear, selectedMonth))
  }

  const rangeInvalid = useCustomRange
    && rangeStart
    && rangeEnd
    && rangeStart > rangeEnd

  if (loading && !data) {
    return <div className={styles.loading}><Spinner /></div>
  }

  const {
    stats,
    recentCampaigns = [],
    selectedCampaign,
    selectedPeriod,
    periodLabel,
    periodMode,
    availableYears = [],
    pagination,
  } = data || {}

  const emailStats = stats?.periodEmailStats || { sent: 0, pending: 0, failed: 0 }
  const emailTotal = emailStats.sent + emailStats.pending + emailStats.failed
  const emailProgress = emailTotal > 0 ? Math.round((emailStats.sent / emailTotal) * 100) : 0

  const isCurrentPeriod = !useCustomRange
    && selectedMonth === CURRENT_MONTH
    && selectedYear === CURRENT_YEAR
  const years = availableYears.length > 0 ? availableYears : [CURRENT_YEAR]
  const activePeriod = selectedPeriod || `${selectedYear}-${String(selectedMonth).padStart(2, '0')}`
  const activePeriodLabel = periodLabel || formatPeriod(activePeriod)
  const totalPages = pagination?.totalPages || 1
  const totalCampaigns = pagination?.total || 0
  const showPagination = recentCampaigns.length > 0 || totalCampaigns > 0

  const pageIds = recentCampaigns.map((c) => c.id)
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id))

  const selectPeriodFromCampaign = (period) => {
    const [year, month] = period.split('-')
    setUseCustomRange(false)
    setRangeStart('')
    setRangeEnd('')
    setSelectedYear(Number(year))
    setSelectedMonth(Number(month))
  }

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    )
  }

  const toggleSelectAllOnPage = () => {
    if (allPageSelected) {
      setSelectedIds((prev) => prev.filter((id) => !pageIds.includes(id)))
    } else {
      setSelectedIds((prev) => [...new Set([...prev, ...pageIds])])
    }
  }

  const executeDeleteCampaigns = async (ids) => {
    if (!ids.length) return
    setDeleting(true)
    try {
      await Promise.all(ids.map((id) => deleteCorporateCampaign(id)))
      openSnackbarRef.current(`${ids.length} campagne(s) supprimée(s)`)
      setSelectedIds((prev) => prev.filter((id) => !ids.includes(id)))
      await load()
    } catch (error) {
      openSnackbarRef.current(error?.response?.data?.message || 'Erreur suppression')
    } finally {
      setDeleting(false)
    }
  }

  const requestDeleteCampaigns = (ids) => {
    if (!ids.length) return
    const label = ids.length === 1 ? 'cette campagne' : `${ids.length} campagnes`
    setConfirmModal({
      ids,
      variant: 'warning',
      title: 'Supprimer la campagne',
      message: `Supprimer ${label} ?`,
      detail: 'Tous les PDF et documents associés seront définitivement supprimés.',
      confirmLabel: 'Supprimer',
    })
  }

  const handleConfirmModal = () => {
    if (!confirmModal) return
    const { ids } = confirmModal
    setConfirmModal(null)
    executeDeleteCampaigns(ids)
  }

  return (
    <div className={styles.page}>
      <div className={styles.topBar}>
        <div>
          <h1>Dashboard Corporate</h1>
          <p>Conventions clients — envoi PDF Sage X3</p>
        </div>
        <button type="button" className={styles.ctaBtn} onClick={() => history.push('/corporate/import')}>
          + Nouvel envoi PDF
        </button>
      </div>

      <div className={styles.periodBar}>
        <label className={styles.periodToggle}>
          <input
            type="checkbox"
            checked={useCustomRange}
            onChange={(e) => (e.target.checked ? enableCustomRange() : setUseCustomRange(false))}
          />
          Période libre
        </label>

        {useCustomRange ? (
          <div className={styles.periodRange}>
            <label className={styles.periodField}>
              <span>Début</span>
              <input
                type="month"
                className={styles.monthInput}
                value={rangeStart}
                onChange={(e) => setRangeStart(e.target.value)}
                aria-label="Début de période"
              />
            </label>
            <span className={styles.periodArrow} aria-hidden="true">→</span>
            <label className={styles.periodField}>
              <span>Fin</span>
              <input
                type="month"
                className={styles.monthInput}
                value={rangeEnd}
                onChange={(e) => setRangeEnd(e.target.value)}
                aria-label="Fin de période"
              />
            </label>
            {rangeInvalid && (
              <span className={styles.periodError}>La date de début doit être avant la fin</span>
            )}
          </div>
        ) : (
          <div className={styles.periodQuick}>
            <div className={styles.filterGroup}>
              <label htmlFor="corp-dash-month">Mois</label>
              <select
                id="corp-dash-month"
                className={styles.filterSelect}
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(Number(e.target.value))}
              >
                {MONTHS.slice(1).map((label, i) => (
                  <option key={label} value={i + 1}>{label}</option>
                ))}
              </select>
            </div>
            <div className={styles.filterGroup}>
              <label htmlFor="corp-dash-year">Année</label>
              <select
                id="corp-dash-year"
                className={styles.filterSelect}
                value={selectedYear}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
              >
                {years.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          </div>
        )}

        {!isCurrentPeriod && !useCustomRange && (
          <button type="button" className={styles.resetBtn} onClick={goToCurrentMonth}>
            Revenir au mois en cours
          </button>
        )}
        {useCustomRange && (
          <button type="button" className={styles.resetBtn} onClick={goToCurrentMonth}>
            Réinitialiser
          </button>
        )}
      </div>

      <div className={styles.periodBanner}>
        <div>
          <span className={styles.periodLabel}>
            {isCurrentPeriod ? 'Période en cours' : 'Période sélectionnée'}
          </span>
          <strong>{activePeriodLabel}</strong>
        </div>
        {periodMode === 'month' && selectedCampaign && (
          <div className={styles.periodStatus}>
            <span className={styles.badgeOk}>Campagne active</span>
            <span>{emailProgress}% emails envoyés</span>
            <button type="button" className={styles.linkBtn} onClick={() => history.push(`/corporate/campagnes/${selectedCampaign._id}`)}>
              Voir détail →
            </button>
          </div>
        )}
        {periodMode === 'range' && stats?.hasCampaign && (
          <div className={styles.periodStatus}>
            <span className={styles.badgeOk}>{stats.totalCampaigns} campagne(s)</span>
            <span>{emailProgress}% emails envoyés</span>
          </div>
        )}
      </div>

      <div className={styles.kpiGrid}>
        <div className={`${styles.kpiCard} ${styles.kpiPrimary}`}>
          <span className={styles.kpiLabel}>Clients convention</span>
          <span className={styles.kpiValue}>{stats?.totalClients || 0}</span>
        </div>
        <div className={styles.kpiCard}>
          <span className={styles.kpiLabel}>PDF période</span>
          <span className={styles.kpiValue}>{stats?.periodDocuments || 0}</span>
        </div>
        <div className={styles.kpiCard}>
          <span className={styles.kpiLabel}>Emails envoyés</span>
          <span className={styles.kpiValue}>{emailStats.sent} / {emailTotal || 0}</span>
        </div>
        <div className={styles.kpiCard}>
          <span className={styles.kpiLabel}>Campagnes période</span>
          <span className={styles.kpiValue}>{stats?.totalCampaigns || 0}</span>
        </div>
      </div>

      <div className={styles.panel}>
        <div className={styles.panelHeader}>
          <div>
            <h2>Campagnes récentes</h2>
            <p className={styles.historyHint}>
              Ouvrir le détail ou supprimer une ou plusieurs campagnes.
            </p>
          </div>
          <div className={styles.panelActions}>
            {selectedIds.length > 0 && (
              <button
                type="button"
                className={styles.deleteBtn}
                disabled={deleting || pageLoading}
                onClick={() => requestDeleteCampaigns(selectedIds)}
              >
                {deleting ? 'Suppression...' : `Supprimer (${selectedIds.length})`}
              </button>
            )}
            {pagination?.total > 0 && (
              <span className={styles.pageInfo}>
                {pagination.total} campagne(s) · page {pagination.page}/{pagination.totalPages}
              </span>
            )}
          </div>
        </div>

        <div className={styles.filterBar} style={{ marginBottom: '1rem', padding: '0.75rem 1rem' }}>
          <label className={styles.filterGroup} style={{ flexDirection: 'row', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={errorsOnly}
              onChange={(e) => setErrorsOnly(e.target.checked)}
              aria-label="Afficher uniquement les campagnes avec erreurs"
            />
            <span>Campagnes avec erreurs uniquement</span>
          </label>
          {errorsOnly && (
            <button
              type="button"
              className={styles.resetBtn}
              onClick={() => setErrorsOnly(false)}
            >
              Réinitialiser le filtre
            </button>
          )}
        </div>

        {rangeInvalid ? (
          <p className={styles.empty}>Période invalide : le mois de début doit être antérieur ou égal au mois de fin.</p>
        ) : recentCampaigns.length === 0 ? (
          <p className={styles.empty}>
            {errorsOnly
              ? 'Aucune campagne avec erreurs.'
              : 'Aucune campagne Corporate'}
          </p>
        ) : (
          <div className={pageLoading ? styles.tableLoading : undefined}>
            <div className="table-scroll-wrap">
            <table className={`${styles.table} responsive-table`}>
              <thead>
                <tr>
                  <th className={styles.checkCol}>
                    <input
                      type="checkbox"
                      checked={allPageSelected}
                      onChange={toggleSelectAllOnPage}
                      aria-label="Sélectionner toutes les campagnes de la page"
                    />
                  </th>
                  <th>Période</th>
                  <th>PDF</th>
                  <th>Erreurs</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {recentCampaigns.map((c) => (
                  <tr
                    key={c.id}
                    className={c.period === activePeriod ? styles.rowActive : undefined}
                  >
                    <td className={styles.checkCol} data-label="Sélection">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(c.id)}
                        onChange={() => toggleSelect(c.id)}
                        aria-label={`Sélectionner ${formatPeriod(c.period)}`}
                      />
                    </td>
                    <td data-label="Période">
                      <button
                        type="button"
                        className={styles.periodLink}
                        onClick={() => selectPeriodFromCampaign(c.period)}
                      >
                        {formatPeriod(c.period)}
                      </button>
                    </td>
                    <td data-label="PDF">{c.successCount}</td>
                    <td data-label="Erreurs">
                      {c.errorCount > 0 ? (
                        <span className={styles.badgeWarn}>{c.errorCount}</span>
                      ) : (
                        c.errorCount
                      )}
                    </td>
                    <td data-label="Actions">
                      <div className={`${styles.actionCell} action-cell-inner`}>
                        <button type="button" className={styles.tableBtn} onClick={() => history.push(`/corporate/campagnes/${c.id}`)}>
                          Ouvrir
                        </button>
                        <button
                          type="button"
                          className={styles.deleteBtnSmall}
                          disabled={deleting}
                          onClick={() => requestDeleteCampaigns([c.id])}
                        >
                          Supprimer
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>

            {showPagination && (
              <div className={styles.pagination}>
                <button
                  type="button"
                  className={styles.pageBtn}
                  disabled={page <= 1 || pageLoading}
                  onClick={() => setPage((p) => p - 1)}
                >
                  ← Précédent
                </button>
                <span className={styles.pageLabel}>
                  Page {pagination?.page || page} sur {totalPages}
                  {' · '}{totalCampaigns} campagne(s)
                </span>
                <button
                  type="button"
                  className={styles.pageBtn}
                  disabled={page >= totalPages || pageLoading}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Suivant →
                </button>
                <select
                  className={styles.pageSizeSelect}
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  aria-label="Nombre de campagnes par page"
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>{size} / page</option>
                  ))}
                </select>
              </div>
            )}
          </div>
        )}
      </div>

      <ConfirmModal
        open={!!confirmModal}
        variant={confirmModal?.variant || 'warning'}
        title={confirmModal?.title}
        message={confirmModal?.message}
        detail={confirmModal?.detail}
        confirmLabel={confirmModal?.confirmLabel || 'Supprimer'}
        onConfirm={handleConfirmModal}
        onCancel={() => setConfirmModal(null)}
        loading={deleting}
      />
    </div>
  )
}

export default CorporateDashboard
